import type { StripeProducts } from '@racemap/sdk/schema/billing';
import { Typography } from 'antd';
import type { BaseType } from 'antd/es/typography/Base';
import type { FC } from 'react';
import { useGetProductPricePerUnit } from 'src/lib/customHooks/useGetProductPricePerUnit';
import { usePriceList } from 'src/lib/customHooks/usePriceList';
import { formatPrice } from 'src/misc/formatPrice';
import { Placeholder } from '../Placeholder/index';

const { Text } = Typography;

interface Props {
  productId: StripeProducts | null;
  units?: number;
  textType?: BaseType;
}

/**
 * Displays the formatted price of a product based on the number of units.
 *
 * @param productId - The Stripe product identifier, or null if the product is free
 * @param units - The number of units to calculate the total price for (default: 1)
 * @param textType - The text style type to apply to the price display (optional)
 *
 * @returns A Text component displaying the formatted price, a Placeholder while loading,
 *          "Free" text if productId is null, or "-" if the price cannot be determined
 *
 * @example
 * <ProductPrice productId="prod_123" units={2} />
 */
export const ProductPrice: FC<Props> = ({ productId, units = 1, textType }) => {
  const { isLoading } = usePriceList();
  const getPricePerUnit = useGetProductPricePerUnit();

  if (productId == null) return <Text type={textType}>Free</Text>;
  if (isLoading) return <Placeholder width={100} />;

  const pricePerUnit = getPricePerUnit(productId);
  if (pricePerUnit == null) return <Text type={textType}>-</Text>;

  return <Text type={textType}>{formatPrice(pricePerUnit, units)}</Text>;
};
